import React, { useEffect, useState } from "react";
import Button from "../Button";
import {
  FlaskConical,
  Search,
  ShoppingCart,
  Trash2,
  XIcon,
} from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Layout from "src/components/app/layout/Layout";
import Header from "src/components/app/layout/Header";
import Card from "../Card/Card";
import {
  addLabTestToCartRequest,
  cartListDeleteRequest,
} from "src/redux/slices/labTestSlice";
import { useAppDispatch } from "src/redux/hooks";
import Spinner from "src/components/common/Spinner";
import { LabTestListData, SearchLabTestResult } from "src/types/labTests.types";
import { useAddToCart } from "src/hooks/useAddLabTestToCart";
import Input from "../Input";
import { useDebounce } from "src/hooks/useDebounce";
import Loader from "src/components/common/Loader";
import { EVENT_NAMES, SCREEN_NAMES } from "src/helpers/analytics/events";
import analytics from "src/helpers/analytics";

export interface TestItem {
  catalog_name: string;
  description: string;
  mrp_price?: number;
  selling_price?: number;
  b2c_price?: number;
  diagnostic_catalog_master_id: string;
}

interface CategoryTestListViewProps {
  category_list?: LabTestListData["catalogs_list"];
  viewAll: boolean;
  testIdsPresentInCart?: { lab_test_name: string }[];
  cartCount?: number;
  onCartUpdate?: () => void;
}

export const testItemToShow = (
  testItem: TestItem,
  index: number,
  addedTestsInCart: { lab_test_name: string; code?: string }[],
  handleAddToCart: (
    diagnostic_catalog_master_id: string,
    catalog_name: string,
    viewAll?: boolean,
  ) => void,
  viewAll?: boolean,
  isTestLoading?: (testId: string) => boolean,
  isAnyTestLoading?: boolean,
  handleRemoveFromCart?: (
    code: string,
    labTestName: string,
    labTestId: string,
  ) => void,
  isRemovingTest?: (code: string) => boolean,
  showFullDetail: boolean = false,
) => {
  const isInCart = addedTestsInCart.some(
    (test: { lab_test_name: string; code?: string }) =>
      test.lab_test_name === testItem.catalog_name,
  );
  const testInCart = addedTestsInCart.find(
    (test: { lab_test_name: string; code?: string }) =>
      test.lab_test_name === testItem.catalog_name,
  );
  return (
    <Card key={index} stackTitleAndDescription={true}>
      <div key={index} className="flex items-start gap-2 bg-white">
        <div className="text-[var(--app-primary)] bg-[var(--icon-bg-primary)] rounded-full p-2">
          <FlaskConical className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-gray-900 leading-snug break-words">
            {testItem.catalog_name}
          </h3>
          <p
            className={`text-xs text-gray-500 mt-1 ${
              showFullDetail ? "" : "line-clamp-2 break-words"
            }`}
          >
            {testItem.description}
          </p>
          <div className="flex items-center justify-between pt-2">
            <p className="text-sm font-semibold text-gray-900">
              {testItem.mrp_price !== undefined ||
              testItem.selling_price !== undefined ||
              testItem.b2c_price !== undefined ? (
                <>
                  ₹
                  {testItem.mrp_price ??
                    testItem.selling_price ??
                    testItem.b2c_price ??
                    "N/A"}
                </>
              ) : (
                <span className="text-gray-500 italic">
                  Check pricing in cart details
                </span>
              )}
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                rounded="full"
                onClick={() => {
                  handleAddToCart(
                    testItem.diagnostic_catalog_master_id,
                    testItem.catalog_name,
                    viewAll,
                  );
                }}
                variant={isInCart ? "contained" : "outlined"}
                disabled={isInCart || isAnyTestLoading}
                loading={isTestLoading?.(testItem.diagnostic_catalog_master_id)}
              >
                {isInCart ? "Added to Cart" : "Add To Cart"}
              </Button>
              {isInCart && handleRemoveFromCart && testInCart?.code && (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    if (
                      testInCart.code &&
                      !isRemovingTest?.(testInCart.code) &&
                      !isAnyTestLoading
                    ) {
                      handleRemoveFromCart(
                        testInCart.code,
                        testInCart.lab_test_name,
                        testItem.diagnostic_catalog_master_id,
                      );
                    }
                  }}
                >
                  <Trash2 className="w-4 h-4 text-red-600" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

const CategoryTestListView: React.FC<CategoryTestListViewProps> = (props) => {
  const location = useLocation();
  const category_list: LabTestListData["catalogs_list"] =
    props.category_list || location.state?.category_list || [];
  const itemsToShow: LabTestListData["catalogs_list"] = props.viewAll
    ? category_list
    : category_list.slice(0, 5);
  const [filteredList, setFilteredList] = useState({
    loading: false,
    items: [],
  });
  const [searchValue, setSearchValue] = useState<string>("");
  const [isDebounceLoading, setIsDebounceLoading] = useState<boolean>(false);
  const params = useParams();
  const supportProgramKey = params["support-program-key"];
  const categoryName = params["catalog-name"];
  const navigate = useNavigate();
  const cartCount = props.cartCount || location.state?.cartCount || 0;
  const dispatch = useAppDispatch();
  const addedTestsInCart =
    props.testIdsPresentInCart || location.state?.testIdsPresentInCart || [];
  const { handleAddToCart, isTestLoading, isAnyTestLoading } = useAddToCart(
    props.onCartUpdate,
  );

  const [removingTestCode, setRemovingTestCode] = useState<string | null>(null);

  const handleRemoveFromCart = (
    code: string,
    labTestName: string,
    labTestId: string,
  ) => {
    // Event: LAB_TEST_REMOVE_CART_CLICKED
    analytics.trackEvent(EVENT_NAMES.LAB_TEST_REMOVE_CART_CLICKED, {
      screen_name: SCREEN_NAMES.BOOK_LAB_TEST_SCREEN,
      support_program_key: supportProgramKey,
      lab_test_name: labTestName,
      lab_test_id: labTestId,
    });
    setRemovingTestCode(code);
    new Promise((resolve, reject) => {
      dispatch(
        cartListDeleteRequest({
          resolve,
          reject,
          payload: { code },
        }),
      );
    })
      .then(() => {
        // Event: LAB_TEST_REMOVE_CART_SUCCESS
        analytics.trackEvent(EVENT_NAMES.LAB_TEST_REMOVE_CART_SUCCESS, {
          screen_name: SCREEN_NAMES.BOOK_LAB_TEST_SCREEN,
          support_program_key: supportProgramKey,
          lab_test_name: labTestName,
          lab_test_id: labTestId,
        });
        props.onCartUpdate?.();
      })
      .catch((err) => {
        // Event: LAB_TEST_REMOVE_CART_ERROR
        analytics.trackEvent(EVENT_NAMES.LAB_TEST_REMOVE_CART_ERROR, {
          screen_name: SCREEN_NAMES.BOOK_LAB_TEST_SCREEN,
          support_program_key: supportProgramKey,
          lab_test_name: labTestName,
          lab_test_id: labTestId,
        });
        console.error("Error removing from cart:", err);
      })
      .finally(() => {
        setRemovingTestCode(null);
      });
  };

  const isRemovingTest = (code: string) => removingTestCode === code;
  const debouncedSearchValue = useDebounce(searchValue, 500);

  useEffect(() => {
    if (debouncedSearchValue.length > 0) {
      setFilteredList({ items: [], loading: true });

      const filteredItems = itemsToShow?.filter((testItem) => {
        return testItem.catalog_name
          .toLowerCase()
          .includes(debouncedSearchValue.toLowerCase());
      });
      setFilteredList({ items: filteredItems as any, loading: false });
      setIsDebounceLoading(false);
    } else {
      setFilteredList({ items: [], loading: false });
      setIsDebounceLoading(false);
    }
  }, [debouncedSearchValue]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);

    if (value.length > 0) {
      setIsDebounceLoading(true);
    } else {
      setIsDebounceLoading(false);
      setFilteredList({ items: [], loading: false });
    }
  };

  if (props.viewAll) {
    return (
      <Layout supportProgramKey={supportProgramKey || ""}>
        <>
          <div className="fixed top-0 left-0 right-0 z-10 max-w-lg mx-auto">
            <Header
              title={categoryName || ""}
              titlePosition="left"
              onBackClick={() => {
                navigate(-1);
              }}
              rightComponent={
                <div
                  className="relative w-6 h-6 mt-2"
                  onClick={() => {
                    if (cartCount === 0) {
                      return;
                    }
                    navigate(
                      `/app/${supportProgramKey}/book-lab-test/checkout`,
                    );
                  }}
                >
                  <ShoppingCart className="w-6 h-6 text-gray-800" />
                  {cartCount > 0 && (
                    <span
                      className="absolute -top-1 -right-1  text-white text-[10px] font-medium rounded-full w-4 h-4 flex items-center justify-center leading-none"
                      style={{ backgroundColor: "var(--app-primary)" }}
                    >
                      {cartCount}
                    </span>
                  )}
                </div>
              }
            />
            <div className="p-4 bg-white border-b border-gray-200">
              <Input
                name="search"
                type="text"
                placeholder="Search"
                fullWidth
                onChange={handleSearchChange}
                value={searchValue}
                startAdornment={<Search className="ml-2 w-4 h-4" />}
                endAdornment={
                  searchValue.length > 0 ? (
                    <button
                      className="border border-gray-300 bg-gray-100 rounded-full p-1"
                      onClick={() => setSearchValue("")}
                    >
                      <XIcon className="w-4 h-4" />
                    </button>
                  ) : null
                }
                disabled={filteredList.loading}
              />
            </div>
          </div>

          <div className="mt-[calc(57px+75px)] p-4">
            <div className="space-y-3">
              {searchValue.length > 0 ? (
                isDebounceLoading || filteredList.loading ? (
                  <div className="text-gray-500 text-sm flex justify-center items-center h-[calc(100vh-220px)]">
                    <Loader size="sm" />
                  </div>
                ) : filteredList.items.length === 0 ? (
                  <div className="text-gray-500 text-sm">
                    No matching results found!
                  </div>
                ) : (
                  filteredList.items.map((testItem, index) =>
                    testItemToShow(
                      testItem,
                      index,
                      addedTestsInCart,
                      handleAddToCart,
                      props.viewAll,
                      isTestLoading,
                      isAnyTestLoading,
                      handleRemoveFromCart,
                      isRemovingTest,
                    ),
                  )
                )
              ) : (
                itemsToShow?.map((testItem, index) =>
                  testItemToShow(
                    testItem,
                    index,
                    addedTestsInCart,
                    handleAddToCart,
                    props.viewAll,
                    isTestLoading,
                    isAnyTestLoading,
                    handleRemoveFromCart,
                    isRemovingTest,
                  ),
                )
              )}
            </div>
          </div>
        </>
      </Layout>
    );
  }

  // Inline (non-viewAll) — avoid Layout wrapper to prevent extra vertical gaps
  return (
    <div className="space-y-3">
      {itemsToShow?.map((testItem, index) =>
        testItemToShow(
          testItem,
          index,
          addedTestsInCart,
          handleAddToCart,
          props.viewAll,
          isTestLoading,
          isAnyTestLoading,
          handleRemoveFromCart,
          isRemovingTest,
        ),
      )}
    </div>
  );
};

export default CategoryTestListView;
